<template>
  <section class="sheet__performance" data-testid="sheet__performance">
    <div class="sheet__performance-header">
      <ProductPerformanceHeader
        v-if="!error"
        :is-loading="isLoading"
        :has-error="error"
        :chart-summary="productPerformance?.periods[0]"
        :period-filters="periodFilters"
        :benchmark-filters="benchmarkFilters"
        profit-label="Rentabilidade"
        :return-label="
          benchmarkFilters.find(filter => filter.active)?.label || ''
        "
        @on-period-filter-change="handlePeriodFilterChange"
        @on-benchmark-filter-change="handleBenchmarkFilterChange"
      />
    </div>
    <HelperViewStatus
      v-if="error"
      title="Ocorreu um erro ao carregar o desempenho do produto"
      description="Tente buscar novamente"
      button-label="Tentar novamente"
      :button-loading="isLoading"
      status="alert"
      @click="fetchProductPerformanceAndFilters"
    />
    <template v-else>
      <LineChart
        data-testid="sheet-performance-chart"
        class="sheet__performance-chart"
        :series="productPerformanceSeries"
        :labels="labels"
        :benchmark="benchmarkData"
        :show-axis-x-labels="false"
        :visible-tooltips-index="[0, 1]"
        :is-loading="isLoading"
        :width="508"
        height="300px"
        :is-reversed-y-axis="false"
      />
      <ChartFooterV2
        :left-text="productPerformance?.chartRange || '-'"
        :is-loading="isLoading"
      />
    </template>
  </section>
</template>

<script setup lang="ts">
import {
  EProductTypes,
  getProductCategoriesFilters,
  getProductPerformancePrePurchase,
  IProductBenchmark,
  IProductPerformanceResponse,
  IProductPeriod
} from '@/modules/catalog/services/products';
import { PropType, computed, onMounted, ref } from 'vue';
import {
  LineChart,
  ChartFooterV2
} from '@/modules/common/components/charts/v2';
import { HelperViewStatus } from '@warrenbrasil/nebraska-web';
import ProductPerformanceHeader from './components/ProductPerformanceHeader.vue';

const props = defineProps({
  categorySelectedProduct: {
    type: String as PropType<EProductTypes>,
    default: EProductTypes.FUNDS
  },
  productApiId: {
    type: String,
    default: ''
  }
});

const productPerformance = ref<IProductPerformanceResponse>();
const benchmarkFilters = ref<IProductBenchmark[]>([]);
const periodFilters = ref<IProductPeriod[]>([]);
const isLoading = ref(false);
const error = ref(false);

const productPerformanceSeries = computed(() => {
  const asset = productPerformance.value?.chart.axisY?.find(
    axis => axis.type === 'Asset'
  );

  return {
    name: asset?.name || '-',
    data: asset?.values.map(item => item?.value) || [],
    comparator: [],
    benchmarkComparison: []
  };
});

const labels = computed(
  () =>
    productPerformance.value?.chart.axisX?.[0]?.values?.map(
      value => value.value
    ) || []
);

const benchmarkData = computed(() => {
  const benchmark = productPerformance.value?.chart.axisY?.find(
    axis => axis.type === 'index'
  );

  return {
    name: benchmark?.name || '-',
    data: benchmark?.values.map(item => item?.value) || []
  };
});

const fetchProductPerformance = async () => {
  isLoading.value = true;
  try {
    const response = await getProductPerformancePrePurchase(
      props.productApiId,
      props.categorySelectedProduct,
      periodFilters.value.find(filter => filter.active)?.value || '',
      benchmarkFilters.value.find(filter => filter.active)?.value || ''
    );
    productPerformance.value = response;
    error.value = false;
  } catch {
    error.value = true;
  } finally {
    isLoading.value = false;
  }
};

async function fetchProductCategoriesFilters() {
  isLoading.value = true;
  try {
    const response = await getProductCategoriesFilters(
      props.categorySelectedProduct
    );
    benchmarkFilters.value = response.benchmarks;
    periodFilters.value = response.periods;
    error.value = false;
  } catch {
    error.value = true;
  } finally {
    isLoading.value = false;
  }
}

const handlePeriodFilterChange = (value: string) => {
  periodFilters.value = periodFilters.value.map(filter => ({
    ...filter,
    active: filter.value === value
  }));
  fetchProductPerformance();
};

const handleBenchmarkFilterChange = (value: string) => {
  benchmarkFilters.value = benchmarkFilters.value.map(filter => ({
    ...filter,
    active: filter.value === value
  }));
  fetchProductPerformance();
};

const fetchProductPerformanceAndFilters = async () => {
  await fetchProductCategoriesFilters();
  await fetchProductPerformance();
};

onMounted(fetchProductPerformanceAndFilters);
</script>

<style scoped lang="less">
.sheet {
  &__performance {
    width: 100%;
    background: @background-secondary;
    padding: @size-spacing-x1000 @size-spacing-x1200 0 @size-spacing-x1200;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  &__performance-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: @size-spacing-x600;
  }

  &__item-title {
    text-align: left;
  }
}
</style>
